// =============================================
// 🛠️ Enhanced Browser Support Check
// =============================================

function isBrowserSupported() {
  const userAgent = navigator.userAgent.toLowerCase();
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  const isCapacitor = userAgent.includes('capacitor');

  const SUPPORTED_BROWSERS = {
    chrome: 90,
    firefox: 89,
    safari: 15,
    edg: 90,
    opera: 76,
    brave: 1.24,
    samsung: 15
  };

  const BLOCKED_BROWSERS = [
    'msie', 'trident',
    'ucbrowser',
    'opera mini',
    'miuibrowser',
    'baidubrowser',
    'qqbrowser',
    'qupzilla',
    'vivaldi'
  ];

  if (isStandalone || isCapacitor) return true;

  const requiredFeatures = [
    'Promise',
    'fetch',
    'IntersectionObserver',
    'Proxy',
    'Map',
    'Set',
    'URL',
    'requestIdleCallback'
  ];

  if (requiredFeatures.some(feat => !(feat in window))) return false;

  const browserData = (() => {
    if (userAgent.includes('chrome') && !userAgent.includes('edg')) {
      const isBrave = navigator.brave && navigator.brave.isBrave;
      const isOpera = userAgent.includes('opr');
      return {
        name: isBrave ? 'brave' : isOpera ? 'opera' : 'chrome',
        version: parseInt(userAgent.match(/chrome\/(\d+)/)?.[1] || 0)
      };
    }
    if (userAgent.includes('safari') && !userAgent.includes('chrome')) {
      return {
        name: 'safari',
        version: parseInt(userAgent.match(/version\/(\d+)/)?.[1] || 0)
      };
    }
    if (userAgent.includes('firefox')) {
      return {
        name: 'firefox',
        version: parseInt(userAgent.match(/firefox\/(\d+)/)?.[1] || 0)
      };
    }
    if (userAgent.includes('edg')) {
      return {
        name: 'edg',
        version: parseInt(userAgent.match(/edg\/(\d+)/)?.[1] || 0)
      };
    }
    if (userAgent.includes('samsungbrowser')) {
      return {
        name: 'samsung',
        version: parseInt(userAgent.match(/samsungbrowser\/(\d+)/)?.[1] || 0)
      };
    }
    return null;
  })();

  if (!browserData) return false;

  const isBlocked = BLOCKED_BROWSERS.some(browser => userAgent.includes(browser));
  if (isBlocked) return false;

  const isSupported = SUPPORTED_BROWSERS[browserData.name] && 
                     browserData.version >= SUPPORTED_BROWSERS[browserData.name];
  return isSupported;
}

// =============================================
// 🚦 Browser Support Gate
// =============================================

if (!isBrowserSupported()) {
  const errorHTML = `
    <div class="browser-error">
      <div class="error-container">
        <h1>⚠️ Browser Not Supported</h1>
        <p><strong>${process.env.VUE_APP_NAME}</strong> requires a modern browser for optimal performance and security.</p>
        <div class="recommendations">
          <h2>Recommended Browsers:</h2>
          <ul>
            <li><a href="https://www.google.com/chrome/" target="_blank">Google Chrome 90+</a></li>
            <li><a href="https://www.mozilla.org/firefox/" target="_blank">Firefox 89+</a></li>
            <li><a href="https://www.apple.com/safari/" target="_blank">Safari 15+</a> (Mac/iOS)</li>
            <li><a href="https://www.microsoft.com/edge" target="_blank">Microsoft Edge 90+</a></li>
          </ul>
        </div>
        <p class="small">If you believe this is an error, please contact support with your browser details.</p>
      </div>
      <style>
        .browser-error {
          font-family: 'Segoe UI', Roboto, sans-serif;
          background: #f8f9fa;
          color: #343a40;
          height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }
        .error-container {
          max-width: 600px;
          background: white;
          padding: 2rem;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }
        h1 { color: #dc3545; margin-top: 0; }
        .recommendations { margin: 2rem 0; }
        ul { padding-left: 1.5rem; }
        a { color: #0d6efd; text-decoration: none; }
        a:hover { text-decoration: underline; }
        .small { font-size: 0.9rem; color: #6c757d; }
      </style>
    </div>
  `;
  document.body.innerHTML = errorHTML;
  throw new Error('UnsupportedBrowserError: The current browser does not meet application requirements');
}
import Vue from 'vue';
import App from './App.vue';
import router from './router'; // Ensure your router is properly set up
import store from './store/store';
import { createPinia, PiniaVuePlugin } from 'pinia';
import Vuetify from 'vuetify';
import { enforceNotificationPermission } from '@/utils/enforceNotificationPermission';
import { initializeOneSignal } from './onesignal';
import GlobalPlugin from './plugins/globalPlugin';
import VueHtmlToPaper from 'vue-html-to-paper';

//import VueVirtualScroller from 'vue-virtual-scroller';
//import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

// Vuetify styles
import 'vuetify/dist/vuetify.min.css';
import '@mdi/font/css/materialdesignicons.css'; // Material Design Icons
import 'roboto-fontface/css/roboto/roboto-fontface.css'; // Roboto font
import './assets/style.css';
import './assets/ios-theme.css';
import * as globalFunctions from './utils/globalFunctions'; // Import global functions
import './registerServiceWorker';

//Vue.config.devtools = false; // Disable Vue Devtools prompt
//Vue.config.productionTip = false; // Disable production tip in the console

// Register Vuetify
Vue.use(Vuetify);

Vue.use(GlobalPlugin);
//Vue.use(VueVirtualScroller);
// VueHtmlToPaper configuration
const vueHtmlToPaperOptions = {
  name: '_blank',
  specs: ['fullscreen=yes', 'titlebar=yes', 'scrollbars=yes'],
  timeout: 2000, // Default timeout before the print window appears
  autoClose: true, // The window will close automatically after printing
  windowTitle: document.title, // Optional: override the window title
};
Vue.use(VueHtmlToPaper, vueHtmlToPaperOptions);

// Attach global functions to Vue prototype
Object.keys(globalFunctions).forEach((functionName) => {
  Vue.prototype[`$${functionName}`] = globalFunctions[functionName];
});

// Ensure permission check runs on app start
store.dispatch('checkNotificationPermission');
enforceNotificationPermission();

// Initialize the app with service worker and OneSignal
const initializeApp = async () => {
  try {
    if ('serviceWorker' in navigator) {
      await registerServiceWorker();
      await initializeOneSignal();
    } else {
      console.warn('Service workers not supported');
      await initializeOneSignal();
    }
  } catch (error) {
    console.error('Initialization failed:', error);
  }
};

const registerServiceWorker = async () => {
  try {
    const registration = await navigator.serviceWorker.register('/service-worker.js');
    console.log('Service Worker registered:', registration.scope);
    
    // Handle updates
    registration.onupdatefound = () => {
      const installingWorker = registration.installing;
      installingWorker.onstatechange = () => {
        if (installingWorker.state === 'installed') {
          if (navigator.serviceWorker.controller) {
            console.log('New content available; please refresh.');
          } else {
            console.log('Content is cached for offline use.');
          }
        }
      };
    };
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    throw error;
  }
};

// PWA installation prompt
if ('serviceWorker' in navigator && 'BeforeInstallPromptEvent' in window) {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    window.deferredPrompt = e;
    console.log('PWA installation available');
  });
}

Vue.use(PiniaVuePlugin);
const pinia = createPinia();

// Create and mount Vue instance
new Vue({
  router,
  store,
  pinia,
  vuetify: new Vuetify(),
  render: (h) => h(App),
  mounted() {
    initializeApp();
  }
}).$mount('#app');